if (!customElements.get("media-gallery")) {
  customElements.define(
    "media-gallery",
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia("(min-width: 750px)");
        // Slides 2-3 render lazy (hb-early-media) so they stay out of the mobile LCP window while they sit
        // offscreen in the slider. Desktop hides inactive slides (display:none), so promote them at once
        // there; on mobile promote at window load or on the first touch of the gallery.
        const promoteEarlyMedia = () => {
          this.querySelectorAll('img.hb-early-media[loading="lazy"]').forEach((img) => {
            img.loading = "eager";
          });
        };
        if (this.mql.matches || document.readyState === "complete") {
          promoteEarlyMedia();
        } else {
          window.addEventListener("load", promoteEarlyMedia, { once: true });
          this.addEventListener("touchstart", promoteEarlyMedia, { once: true, passive: true });
          this.addEventListener("pointerdown", promoteEarlyMedia, { once: true, passive: true });
        }
        this.initPaginationDots();
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener("slideChanged", debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll("[data-target]").forEach((mediaToSwitch) => {
          const thumbnailButton = mediaToSwitch.querySelector("button");
          thumbnailButton.addEventListener("click", this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          // Gallery media 4+ are loading="lazy" and hidden while inactive on desktop, so start the
          // download as soon as the shopper shows intent (hover / keyboard focus) rather than on click.
          const promoteLazyMedia = () => {
            const slide = this.elements.viewer.querySelector(`[data-media-id="${mediaToSwitch.dataset.target}"]`);
            slide?.querySelectorAll('img[loading="lazy"]').forEach((img) => {
              img.loading = "eager";
            });
          };
          thumbnailButton.addEventListener("pointerenter", promoteLazyMedia, { once: true });
          thumbnailButton.addEventListener("focus", promoteLazyMedia, { once: true });
        });

        window.addEventListener("load", () => this.safelyHideLCPImages());
      }

      safelyHideLCPImages() {
        const gallery = this.querySelector(".product-media-container");
        const lcpWrappers = this.querySelectorAll(".product__lcp-image-wrapper");

        if (!gallery || lcpWrappers.length === 0) return;

        requestAnimationFrame(() => {
          setTimeout(() => {
            lcpWrappers.forEach((wrapper) => {
              wrapper.style.visibility = "hidden";
              wrapper.style.position = "absolute";
              wrapper.style.width = "1px";
              wrapper.style.height = "1px";
              wrapper.style.overflow = "hidden";
              wrapper.setAttribute("aria-hidden", "true");
            });
          }, 50);
        });
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`,
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector("[data-media-id]");
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll("[data-media-id]").forEach((element) => {
          element.classList.remove("is-active");
        });
        activeMedia?.classList?.add("is-active");

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail?.parentElement?.firstChild !== activeThumbnail &&
              activeThumbnail?.parentElement?.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: this.getSlidePosition(activeMedia.parentElement, activeMedia) });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: "smooth" });
        });
        this.playActiveMedia(activeMedia);

        const slideIndex = Array.from(this.elements.viewer.querySelectorAll("[data-media-id]")).indexOf(activeMedia);
        if (slideIndex >= 0) this.updatePaginationDots(slideIndex + 1);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail?.dataset?.mediaPosition);
      }

      getSlidePosition(slider, slide) {
        if (getComputedStyle(slider).direction !== "rtl") return slide.offsetLeft;
        const firstSlide = slider.querySelector('[id^="Slide-"]');
        return firstSlide ? slide.offsetLeft + slide.offsetWidth - (firstSlide.offsetLeft + firstSlide.offsetWidth) : 0;
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll("button")
          .forEach((element) => element.removeAttribute("aria-current"));
        thumbnail.querySelector("button").setAttribute("aria-current", true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: this.getSlidePosition(this.elements.thumbnails.slider, thumbnail) });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector(".product__modal-opener--image img");
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace("[index]", position);
          setTimeout(() => {
            this.elements.liveRegion.innerHTML = "";
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector(".deferred-media");
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector("sticky-header");
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event("preventHeaderReveal"));
      }

      initPaginationDots() {
        this.paginationDots = this.querySelectorAll(".gallery-dot");
        if (!this.paginationDots.length) return;
        this.dotNavInProgress = false;
        this.dotsWindowStart = null;

        // The quick view viewer has no prev/next buttons, so SliderComponent stops before it tracks a
        // current page there; work the slide out from the scroll position instead of lighting no dot.
        const pageFromScroll = (slider) => {
          const items = this.elements.viewer.sliderItems || [];
          const step = items.length > 1 ? items[1].offsetLeft - items[0].offsetLeft : 0;
          return step ? Math.round(slider.scrollLeft / step) + 1 : 1;
        };

        const updateDots = () => {
          // A cloned gallery upgrades before its inner slider-component, so the viewer may not have
          // its slider yet.
          const { slider, currentPage } = this.elements.viewer;
          if (!this.dotNavInProgress && slider) {
            const maxScroll = slider.scrollWidth - slider.clientWidth;
            const atEnd = !this.mql.matches && maxScroll > 0 && Math.abs(slider.scrollLeft) >= maxScroll - 1;
            this.updatePaginationDots(atEnd ? this.paginationDots.length : currentPage || pageFromScroll(slider));
          }
        };

        this.elements.viewer.addEventListener("slideChanged", updateDots);
        this.elements.viewer.querySelector('[id^="Slider-"]').addEventListener("scroll", updateDots);
        // A shade swap re-inserts a cached clone of an earlier gallery with the dot it had lit, while its
        // slider starts again at the first slide. The quick view viewer never fires slideChanged to put
        // that right, so resync once the clone is in the page.
        requestAnimationFrame(() => {
          if (this.isConnected) updateDots();
        });

        this.paginationDots.forEach((dot) => {
          dot.addEventListener("click", () => {
            const index = parseInt(dot.dataset.slideIndex, 10);
            const targetSlide = this.elements.viewer.sliderItems?.[index - 1];
            if (!targetSlide) return;
            this.dotNavInProgress = true;
            this.updatePaginationDots(index);
            this.elements.viewer.slider.scrollTo({
              left: this.getSlidePosition(this.elements.viewer.slider, targetSlide),
              behavior: "smooth",
            });
            clearTimeout(this.dotNavTimeout);
            this.dotNavTimeout = setTimeout(() => {
              this.dotNavInProgress = false;
            }, 600);
          });
        });
      }

      updatePaginationDots(activePage) {
        this.paginationDots?.forEach((dot) => {
          const isActive = parseInt(dot.dataset.slideIndex, 10) === activePage;
          dot.classList.toggle("is-active", isActive);
          // The class alone is visual, so screen readers also get the current slide. This runs on every
          // scroll event; only a real change touches the attribute.
          if (dot.hasAttribute("aria-current") !== isActive) {
            if (isActive) dot.setAttribute("aria-current", "true");
            else dot.removeAttribute("aria-current");
          }
        });
        this.updateDotsWindow(activePage);
      }

      // More slides than data-max-dots: only that many dots show, in a window that keeps the current one in the
      // middle where it can (Instagram style). The window's end dots shrink while more slides are hidden past
      // them, and the dots outside it are hidden. The first slide's state comes from the server
      // (snippets/product-media-gallery.liquid). This runs on every scroll event; only a new window touches the DOM.
      updateDotsWindow(activePage) {
        if (!this.paginationDots?.length) return;
        const row = this.paginationDots[0].parentElement;
        const max = parseInt(row.dataset.maxDots, 10);
        const count = this.paginationDots.length;
        if (!max || count <= max) return;
        const start = Math.min(Math.max(activePage - 1 - Math.floor(max / 2), 0), count - max);
        if (start === this.dotsWindowStart) return;
        this.dotsWindowStart = start;
        const hiddenBefore = start > 0;
        const hiddenAfter = start + max < count;
        row.style.setProperty("--gallery-dots-shift", start);
        this.paginationDots.forEach((dot, index) => {
          const slot = index - start;
          dot.classList.toggle("gallery-dot--out", slot < 0 || slot >= max);
          dot.classList.toggle("gallery-dot--xs", (hiddenBefore && slot === 0) || (hiddenAfter && slot === max - 1));
          dot.classList.toggle("gallery-dot--sm", (hiddenBefore && slot === 1) || (hiddenAfter && slot === max - 2));
        });
      }
    },
  );
}
